import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import ActionButton from '@/components/UI/ActionButton';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import type { ResultOf } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import {
  getOrganizationMemberRole,
  getOrganizationMemberSettingsActions,
} from '@/screens/OrganizationMemberUpdate/OrganizationMemberUpdate.utils';
import { getTeamMemberSettingsActions } from '@/screens/TeamPersonUpdate/TeamPersonUpdate.utils';
import formatRoleLabel from '@/utils/formatRoleLabel';
import type { GetPersonSelectedContext } from './PersonContextQueries';
import type { PersonScope } from './PersonScope';

type MembershipInfoRow = { id: string; field: string; value: string };

export default function PersonScopeMembership({
  personId,
  isManaged,
  scope,
  data,
}: {
  personId: string;
  isManaged: boolean;
  scope: PersonScope;
  data: ResultOf<typeof GetPersonSelectedContext>;
}) {
  const { push } = useRouter();
  const userId = useUserId();
  const teamMembership = data.teamMemberships[0];
  const team = teamMembership?.team;
  const event = data.eventContexts[0]?.event;
  const eventMembership = event?.event_people[0];
  const isOrganizationOwner = event?.owner_id === personId;
  const teamActions = getTeamMemberSettingsActions({
    isManaged,
    isSelf: personId === userId,
    permissions: team?.permissions,
    role: teamMembership?.role,
  });
  const eventActions = getOrganizationMemberSettingsActions({
    isManaged,
    isSelf: personId === userId,
    isOwner: isOrganizationOwner,
    permissions: event?.permissions,
  });
  const canEdit =
    scope.kind === 'team'
      ? !!teamMembership && Object.values(teamActions).some(Boolean)
      : !!eventMembership && Object.values(eventActions).some(Boolean);
  const role =
    scope.kind === 'team'
      ? teamMembership?.role
      : eventMembership || isOrganizationOwner
        ? getOrganizationMemberRole({
            assignedRole: eventMembership?.role,
            organizationOwnerId: event?.owner_id,
            personId,
          })
        : undefined;
  const rows: MembershipInfoRow[] = [
    ...(role
      ? [{ id: 'role', field: 'Role', value: formatRoleLabel(role) }]
      : []),
    ...(teamMembership
      ? [
          {
            id: 'participation',
            field: 'Participation',
            value:
              teamMembership.member_type === 'participant'
                ? 'Participating'
                : 'Not participating',
          },
        ]
      : []),
  ];
  return (
    <Box
      direction="horizontal"
      align="center"
      space="md"
      px="md"
      py="sm"
      style={styles.membership}
      testID="PersonScopeMembership"
    >
      <Box flex minWidth={0} space="xs">
        {rows.map((row) => (
          <Text
            size="sm"
            key={row.id}
            testID={`PersonMembershipInfo-${row.id}`}
          >
            <Text size="sm" variant="typographySecondary">
              {row.field}:{' '}
            </Text>
            {row.value}
          </Text>
        ))}
      </Box>
      {canEdit && scope.kind !== 'none' && (
        <ActionButton
          iconOnly
          icon="create-outline"
          variant="secondary"
          iconVariant="typographySecondary"
          style={styles.editAction}
          accessibilityLabel="Edit membership"
          testID="PersonScopeEditMembership"
          onPress={() =>
            push(
              scope.kind === 'team'
                ? {
                    pathname: '/team/[teamId]/member/[personId]/edit',
                    params: { personId, teamId: scope.id },
                  }
                : {
                    pathname:
                      '/organization/[organizationId]/member/[personId]/edit',
                    params: { personId, organizationId: scope.id },
                  },
            )
          }
        />
      )}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  editAction: {
    borderColor: theme.colors.borderMuted,
    flexShrink: 0,
    width: theme.size(11),
    minHeight: theme.size(11),
    paddingHorizontal: 0,
  },
  membership: {
    flexGrow: 1,
    alignItems: 'center',
  },
}));
